import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { XIcon, ZoomInIcon, ZoomOutIcon, MoveIcon } from 'lucide-react';
interface ImageCropperProps {
  imageFile: File;
  onCropComplete: (croppedImageUrl: string) => void;
  onCancel: () => void;
}
export function ImageCropper({
  imageFile,
  onCropComplete,
  onCancel
}: ImageCropperProps) {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({
    x: 0,
    y: 0
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const reader = new FileReader();
    reader.onload = e => {
      setImageSrc(e.target?.result as string);
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile]);
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  const handleCrop = () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Set canvas to square dimensions
    const size = 800;
    canvas.width = size;
    canvas.height = size;
    // Calculate the crop area
    const containerSize = 400; // Preview container size
    const scale = size / containerSize;
    const scaledZoom = zoom * scale;
    // Calculate source dimensions to maintain aspect ratio
    const sourceSize = size / scaledZoom;
    const sourceX = -position.x * scale / scaledZoom;
    const sourceY = -position.y * scale / scaledZoom;
    // Draw the cropped image
    ctx.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, size, size);
    // Convert to blob and create URL
    canvas.toBlob(blob => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        onCropComplete(url);
      }
    }, 'image/jpeg', 0.9);
  };
  return <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" data-id="element-3134">
      <Card className="w-full max-w-2xl" data-id="element-3135">
        <div className="p-6" data-id="element-3136">
          <div className="flex items-center justify-between mb-6" data-id="element-3137">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-3138">Crop Image</h3>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" data-id="element-3139">
              <XIcon className="w-5 h-5" data-id="element-3140" />
            </button>
          </div>

          {/* Crop Preview */}
          <div className="mb-6" data-id="element-3141">
            <div className="relative w-full aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-move border-2 border-gray-300" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} style={{
            maxWidth: '400px',
            margin: '0 auto'
          }} data-id="element-3142">
              {imageSrc && <>
                  <img ref={imageRef} src={imageSrc} alt="Crop preview" className="absolute pointer-events-none" style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                transformOrigin: 'top left',
                maxWidth: 'none'
              }} onLoad={e => {
                const img = e.currentTarget;
                const containerSize = 400;
                const scale = Math.max(containerSize / img.naturalWidth, containerSize / img.naturalHeight);
                setZoom(scale);
              }} data-id="element-3143" />
                  <div className="absolute inset-0 pointer-events-none border-2 border-dashed border-white opacity-50" data-id="element-3144" />
                </>}
            </div>
            <p className="text-xs text-gray-500 text-center mt-2 flex items-center justify-center gap-1" data-id="element-3145">
              <MoveIcon className="w-3 h-3" data-id="element-3146" />
              Drag to reposition • Use zoom controls to adjust size
            </p>
          </div>

          {/* Zoom Controls */}
          <div className="mb-6" data-id="element-3147">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3148">
              Zoom
            </label>
            <div className="flex items-center gap-3" data-id="element-3149">
              <button onClick={() => setZoom(Math.max(0.5, zoom - 0.1))} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3150">
                <ZoomOutIcon className="w-4 h-4" data-id="element-3151" />
              </button>
              <input type="range" min="0.5" max="3" step="0.1" value={zoom} onChange={e => setZoom(parseFloat(e.target.value))} className="flex-1" data-id="element-3152" />
              <button onClick={() => setZoom(Math.min(3, zoom + 0.1))} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3153">
                <ZoomInIcon className="w-4 h-4" data-id="element-3154" />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3" data-id="element-3155">
            <Button variant="outline" onClick={onCancel} className="flex-1" data-id="element-3156">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCrop} className="flex-1" data-id="element-3157">
              Crop & Upload
            </Button>
          </div>
        </div>
      </Card>

      {/* Hidden canvas for cropping */}
      <canvas ref={canvasRef} className="hidden" data-id="element-3158" />
    </div>;
}